/**
 * Cliente 360 (D03, §6/§11.2) — a tela mais pesada do produto, numa
 * requisição.
 *
 * ── Por bloco, o que é fresco e o que é projeção ───────────────────────────
 *
 * A D03 manda decidir isso bloco a bloco, e a régua é a volatilidade:
 *
 *   pessoa         FRESCO — nome e contato são o que o vendedor acabou de
 *                  corrigir no Twenty
 *   empresa        PROJEÇÃO — "dado comercial que muda pouco tolera
 *                  projeção" é o exemplo literal da task
 *   oportunidades  FRESCO — a etapa é o campo que a A07 (D25f) marca como
 *                  "não tolera projeção": o vendedor moveu agora
 *   tarefas        FRESCO — prazo e status mudam no dia (ADR-0008: a tarefa
 *                  é autoritativa no Twenty)
 *   conversas      PROJEÇÃO — vêm do Chatwoot, e a projeção da C19 é
 *                  atualizada por webhook em segundos
 *   timeline       PROJEÇÃO — ver abaixo
 *
 * Cada bloco carrega o próprio `stale`, porque cada um degrada sozinho:
 * Twenty fora deixa pessoa e oportunidades defasadas e não encosta nas
 * conversas.
 *
 * ── A timeline agora vem de `read.timeline` (D06) ──────────────────────────
 *
 * Na D03 este bloco tinha UMA origem (`read.messages`) e dizia isso em
 * `cobertura: 'mensagens'`, com a promessa de que o campo viraria `completa`
 * quando a D06 materializasse as cinco origens. É o que aconteceu.
 *
 * O item passou a ser o MESMO da rota `/v1/timeline` — uma forma só para as
 * duas telas. Duas formas para a mesma coisa é como elas divergem: a tela de
 * detalhe começaria a mostrar campo que a rota não tem, e o contrário.
 */

import { z } from 'zod';
import { MudancaDeCamposSchema, ValoresDeCamposSchema } from './campos-personalizados.js';
import { IdSchema } from './comum.js';
import { ItemDaTimelineSchema } from './timeline.js';
import { InstanteSchema } from './valores.js';

/** Carimbo de frescor por bloco (§14.3) — o mesmo formato em todos. */
const FrescorSchema = z.object({
  stale: z.boolean(),
  projetado_em: InstanteSchema.nullable(),
});

export const ClienteDo360Schema = z.object({
  /** Canônico (§5.1) — o id da URL, ecoado para quem monta links. */
  id: IdSchema,
  nome_exibicao: z.string().nullable(),
  email: z.string().nullable(),
  telefone_exibicao: z.string().nullable(),
  cargo: z.string().nullable(),
  cidade: z.string().nullable(),
  /**
   * Só a EXISTÊNCIA, como na C24: ver o documento exige o step-up da B17
   * (A07 D14f/D15f), e uma tela que abre dezenas de vezes por dia não é
   * lugar de CPF por padrão.
   */
  documento: z.object({ presente: z.boolean() }),
  criado_em: InstanteSchema.nullable(),
  /**
   * Por onde este cliente CHEGOU — o canal da conversa mais antiga dele.
   *
   * ── Por que a mais antiga, e não a mais recente ─────────────────────────
   *
   * A pergunta é "de onde ele veio", e origem não muda: quem chegou pelo
   * WhatsApp e hoje fala pelo Instagram continua tendo vindo do WhatsApp. A
   * conversa mais recente responderia outra pergunta — "por onde ele fala
   * agora" —, e as duas juntas no mesmo campo fariam o número de leads por
   * origem mudar sozinho de uma semana para a outra.
   *
   * `null` quando o cliente nunca conversou: veio de importação ou de
   * conector, e inventar um canal seria pior que a ausência.
   *
   * O canal FINO da matriz da A03 (`whatsapp`, `instagram`), nunca o
   * `Channel::*` cru — quem traduz para palavra é a tela.
   */
  origem: z
    .object({
      canal: z.string().nullable(),
      /** Quando a primeira conversa começou. É a data de chegada. */
      em: InstanteSchema.nullable(),
    })
    .nullable(),
  /**
   * As etiquetas de TODAS as conversas deste cliente, sem repetir.
   *
   * ── Por que somadas aqui, e não só por conversa ────────────────────────
   *
   * Porque a pergunta que se faz olhando um cliente é "o que sabemos sobre
   * ele?", e não "o que sabemos sobre a terceira conversa dele". Um cliente
   * com quatro atendimentos obrigaria a abrir os quatro para descobrir que
   * ele está marcado como `pos-venda`.
   *
   * A ordem é por FREQUÊNCIA: a etiqueta que aparece em três das quatro
   * conversas diz mais sobre o cliente do que a que apareceu uma vez.
   *
   * Continuam existindo por conversa, em `conversas.itens[].etiquetas` — as
   * duas respondem perguntas diferentes.
   */
  etiquetas: z.array(z.string()),
  /**
   * As etiquetas postas NO CLIENTE, e não herdadas de conversa nenhuma.
   *
   * ── Por que um campo separado, e não juntas em `etiquetas` ─────────────
   *
   * Porque só estas se editam. Juntas, a tela teria de oferecer o botão de
   * remover em todas — e remover uma que veio de conversa não faria nada: a
   * próxima projeção a traria de volta. Um botão que desfaz sozinho o que
   * você acabou de fazer é pior que um botão ausente.
   *
   * Separadas, a tela diz a verdade em silêncio: o que dá para tirar tem o
   * `x`, o que é reflexo do atendimento não tem.
   *
   * ── E por que elas existem ─────────────────────────────────────────────
   *
   * `etiquetas` é derivada das conversas, então cliente que nunca conversou
   * não tem nenhuma — e é justamente ele, o que chegou pelo cadastro ou pelo
   * portal, sobre quem a equipe menos sabe. Estas são o assunto escrito à
   * mão, que não depende de ter havido atendimento.
   */
  etiquetas_proprias: z.array(z.string()),
  /** Os campos personalizados do cliente (#568), pelo id do campo. */
  campos: ValoresDeCamposSchema,
  frescor: FrescorSchema,
});

export const EmpresaDo360Schema = z.object({
  referencia_externa: z.string(),
  nome: z.string().nullable(),
  dominio: z.string().nullable(),
  cidade: z.string().nullable(),
  frescor: FrescorSchema,
});

export const OportunidadeDo360Schema = z.object({
  /** Canônico quando já cunhado (C13); `null` enquanto só a engine conhece. */
  id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  /** O valor gravado (`F_AGENDAMENTO_NOVO`): chave, não vai para a tela. */
  etapa: z.string().nullable(),
  /**
   * O nome que se lê ("Novo · Agendamento"), pronto: ver `nomesDasEtapas`.
   * Opcional pela régua do `/v1`: sem ele, a tela mostra a `etapa`.
   */
  etapa_nome: z.string().nullable().optional(),
  valor: z.object({ quantia_micros: z.number(), moeda: z.string() }).nullable(),
  fechamento_previsto_em: InstanteSchema.nullable(),
  criada_em: InstanteSchema.nullable(),
});

export const ConversaDo360Schema = z.object({
  id: IdSchema,
  referencia_externa: z.string(),
  situacao: z.string(),
  /** As desta conversa. A soma do cliente está em `cliente.etiquetas`. */
  etiquetas: z.array(z.string()),
  canal: z.string().nullable(),
  caixa: z.string().nullable(),
  nao_lidas: z.number().int(),
  ultima_mensagem_em: InstanteSchema.nullable(),
});

export const TarefaDo360Schema = z.object({
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  status: z.string().nullable(),
  /**
   * "Atrasada" NÃO vem daqui: depende do horário de atendimento do tenant
   * (A07 Q04), e congelar a derivação no contrato erraria toda vez que o
   * horário mudasse. Quem exibe compara com o relógio da revenda.
   */
  prazo_em: InstanteSchema.nullable(),
});

/** Bloco paginado: itens, quanto existe ao todo, e como pedir o resto. */
const paginado = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    itens: z.array(item),
    total: z.number().int(),
    tem_mais: z.boolean(),
    cursor_proximo: z.string().nullable(),
  });

export const Cliente360Schema = z.object({
  cliente: ClienteDo360Schema,
  /** A maioria dos clientes de revenda é pessoa física (A07 D20f). */
  empresa: EmpresaDo360Schema.nullable(),
  oportunidades: paginado(OportunidadeDo360Schema).extend({ frescor: FrescorSchema }),
  conversas: paginado(ConversaDo360Schema).extend({ frescor: FrescorSchema }),
  tarefas: paginado(TarefaDo360Schema).extend({ frescor: FrescorSchema }),
  timeline: paginado(ItemDaTimelineSchema).extend({
    frescor: FrescorSchema,
    /**
     * `completa` desde a D06. O valor continua no contrato — e não virou
     * constante — porque ele responde a pergunta que a tela precisa fazer:
     * "esta timeline agrega tudo, ou só mensagens?". Dois dos cinco tipos
     * ainda não têm origem (nota e evento de portal), e quando tiverem a
     * resposta não muda de forma.
     */
    cobertura: z.enum(['mensagens', 'completa']),
  }),
});

export type Cliente360 = z.infer<typeof Cliente360Schema>;
export type OportunidadeDo360 = z.infer<typeof OportunidadeDo360Schema>;
export type ConversaDo360 = z.infer<typeof ConversaDo360Schema>;
export type TarefaDo360 = z.infer<typeof TarefaDo360Schema>;

/**
 * Editar o cadastro do cliente, pela ficha do 360 (H08).
 *
 * Só o que o motor de CRM guarda em PESSOA: nome, e-mail, telefone e cargo.
 * Cidade fica de fora porque `person.city` não existe no Twenty — a A01 §6
 * registra o erro e a A10 §6.5 o verificou; cidade, lá, é da empresa.
 *
 * Campo ausente é "não mexer"; `null` é APAGAR. A diferença importa: um
 * formulário que manda tudo a cada salvamento apagaria o que não foi tocado.
 */
export const AtualizarClienteSchema = z
  .object({
    nome: z.string().trim().min(1).max(200).optional(),
    email: z.string().trim().email().max(200).nullable().optional(),
    telefone: z.string().trim().max(40).nullable().optional(),
    cargo: z.string().trim().max(120).nullable().optional(),
    /**
     * CPF ou CNPJ, do jeito que a pessoa digitar — com ponto, traço ou nada.
     * A API valida o dígito verificador e guarda só o hash e o número cifrado:
     * documento inválido vira busca que nunca casa, e "quase certo" é pior que
     * ausente (C10, §5.2).
     */
    documento: z.string().trim().max(30).nullable().optional(),
    /** Só os campos personalizados que mudaram (#568); `null` esvazia. */
    campos: MudancaDeCamposSchema.optional(),
  })
  .refine((corpo) => Object.values(corpo).some((campo) => campo !== undefined), {
    message: 'Informe ao menos um campo para alterar.',
  });
export type AtualizarCliente = z.infer<typeof AtualizarClienteSchema>;

/** O cadastro como ficou, já com o que o motor normalizou. */
/**
 * Criar um cliente à mão.
 *
 * ── O nome é o único obrigatório, e não é arbitrário ──────────────────────
 *
 * Sem nome o cliente some da lista e da busca — vira uma linha que existe e
 * que ninguém acha. Contato é opcional porque quem cadastra na frente do
 * balcão às vezes só tem o nome, e obrigar um telefone faria a pessoa inventar
 * um.
 *
 * ── Por que não há campo de responsável ───────────────────────────────────
 *
 * Cliente NÃO TEM DONO no modelo (§5.1). Quem tem responsável é a conversa e a
 * oportunidade, e um campo aqui prometeria uma atribuição que nada guardaria.
 */
export const CriarClienteSchema = z.object({
  nome: z.string().trim().min(1).max(200),
  email: z.string().trim().max(200).nullish(),
  telefone: z.string().trim().max(40).nullish(),
  cargo: z.string().trim().max(120).nullish(),
  /**
   * CPF ou CNPJ, do jeito que a pessoa digitar. A API valida o dígito
   * verificador e guarda só o hash e o número cifrado.
   */
  documento: z.string().trim().max(30).nullish(),
  /** Os campos personalizados já preenchidos no cadastro (#568). */
  campos: MudancaDeCamposSchema.optional(),
});

export const ClienteAtualizadoSchema = z.object({
  id: z.string(),
  nome_exibicao: z.string().nullable(),
  email: z.string().nullable(),
  telefone_exibicao: z.string().nullable(),
  cargo: z.string().nullable(),
  /**
   * Só a existência, como no 360: o número volta cifrado no banco e ver de
   * novo exige o step-up da B17. Ecoá-lo aqui daria a quem edita um caminho
   * curto para ler o documento de qualquer cliente.
   */
  documento: z.object({ presente: z.boolean() }),
  /**
   * Quantos outros cadastros o documento informado fez o sistema suspeitar
   * (D23). Zero é o caso comum.
   *
   * ── Por que a tela precisa deste número ─────────────────────────────────
   *
   * Para dizer a coisa CERTA depois de salvar: que a suspeita foi para a fila
   * de revisão, e NUNCA que os cadastros foram unidos. A Armadilha da C10 é
   * exatamente essa confusão — documento igual dá 0,95, acima do limiar da
   * maioria das revendas, e quem lê "encontramos um cadastro igual" conclui
   * que algo já foi feito.
   *
   * Nada foi. Quem decide fundir é gente, na fila da D09, e é isso que a frase
   * precisa deixar claro enquanto a pessoa ainda está na ficha.
   */
  candidatos_de_fusao: z.number().int().min(0),
});
export type ClienteAtualizado = z.infer<typeof ClienteAtualizadoSchema>;
export type CriarCliente = z.infer<typeof CriarClienteSchema>;
